import React, { useEffect, useState } from 'react';
import Loader from '@session/components/Common/Loader';
import { useAppStore } from '@session/stores/app';
import { config } from '../../config';
import { disengageExtension } from '../../helpers/helpers';
import { useLogger } from '@ui/shared/components/Logger';
import  { ERROR_CODES }  from '@platform-shared/lib/constants';
import PageError from '../../components/PageError'

const SessionRedirect = () => {
  const { apiClient, getStoredParams} = useAppStore();
  const logger = useLogger();
  const [pageErrorText, setPageErrorText] = useState('');
  const [pageErrorCode, setPageErrorCode] = useState('');

  useEffect(() => {
    const initSessionRedirect = async () => {
      try {
        const params = getStoredParams();
        if (!params) {
          return;
        }
        const allowedStatuses = ['onboarding', 'in progress', 'pending reconnection', 'scheduled'];
        
        // first ensure the extension is disengaged...
        const disengaged = await disengageExtension();

        if (disengaged) {
          // log that we had to disengage the extension at this phase...
          logger.logInfo('SessionRedirect', 'UI', 'Disengaged extension at session redirect initialization');
        }

        if(apiClient){
          // now get the session data...
          const sessionData = await apiClient.getSession();

          if(sessionData){
            const status = sessionData?.status.toLowerCase();

            logger.logInfo('SessionRedirect', 'UI','User redirected to to the session redirect page.', null, { 
              reason: useAppStore.getState().initialQueryParams?.reason || "unknown",
              referrer: useAppStore.getState().initialQueryParams?.referrer || "unknown",
            });

            if (!status || !allowedStatuses.includes(status)) {
              setPageErrorText('Session is not in a valid state. Please close this window and start over.');
              setPageErrorCode(ERROR_CODES.UI.SESSION_REDIRECT_INVALID_STATE.CODE);
              logger.logError('SessionRedirect', 'UI', 'Session is not in a valid state. Please close this window and start over.', null, {code: ERROR_CODES.UI.SESSION_REDIRECT_INVALID_STATE.CODE});
            }else{

              sessionStorage.setItem('spMediaRoomCreated', JSON.stringify(false));

              if (status.toLowerCase() === 'in progress') {
                // The session is in progress, so they are ending their session
                await apiClient.endSession({ reason: 'extension-non-responsive' });
                await apiClient.modifyJourneySteps({
                  completeCurrentStep: true,
                  outcomeData: {
                    reason: 'Extension Non-Responsive',
                  },
                  steps: [
                    {
                      stepId: 'ExtensionNonResponsive',
                      action: 'addNext'
                    }
                  ]}
                );
                const redirectUrl = `${config.session.sessionUiUrl}/offboarding?sessionSid=${params.sessionSid}?examSid=${params.examSid}?installSid=${params.installSid}?token=${params.token}`;
                window.location.href = redirectUrl;
              } else {
                await apiClient.modifyJourneySteps({
                  steps: [
                    {
                      stepId: 'ShareScreen',
                      action: 'repair'
                    },
                    {
                      stepId: 'ShareWebcam',
                      action: 'repair'
                    },
                    {
                      stepId: 'ShareMicrophone',
                      action: 'repair'
                    }
                  ]}
                );
                const redirectUrl = `${config.session.sessionUiUrl}/onboarding?sessionSid=${params.sessionSid}?examSid=${params.examSid}?installSid=${params.installSid}?token=${params.token}`;
                window.location.href = redirectUrl;
              }
            }
          }else{
            setPageErrorText('Session is not in a valid state. Please close this window and start over.');
            setPageErrorCode(ERROR_CODES.UI.SESSION_REDIRECT_INVALID_STATE.CODE);
            logger.logError('SessionRedirect', 'UI', 'Session is not in a valid state. Please close this window and start over.', null, {code: ERROR_CODES.UI.SESSION_REDIRECT_INVALID_STATE.CODE});
          }
        }
      } catch (error) {
        setPageErrorText('An error occurred while redirecting to the session. Please close this window and start over.');
        setPageErrorCode(ERROR_CODES.UI.SESSION_REDIRECT_CATCH.CODE);
        logger.logError('SessionRedirect', 'UI', 'An error occurred while redirecting to the session', error, {code: ERROR_CODES.UI.SESSION_REDIRECT_CATCH.CODE});
      }
    };
    initSessionRedirect();
  }, [apiClient, getStoredParams]);

  if (pageErrorText) {
    return <PageError errorText={pageErrorText} 
                      showRefreshButton={false} 
                      errorCode={pageErrorCode} />
  }

  return <Loader 
            size="large"
            onTimeout={() => {
              setPageErrorText('Session is not in a valid state. Please close this window and start over.');
              setPageErrorCode(ERROR_CODES.UI.SESSION_REDIRECT_TIMEOUT.CODE);
              logger.logError('SessionRedirect', 'UI', 'Redirect Timed Out', null, {code: ERROR_CODES.UI.SESSION_REDIRECT_TIMEOUT.CODE});
            }}
            timeout={5}
             />
};

export default SessionRedirect;